<template>
  <el-dialog
      :model-value="modelValue"
      :title="isEdit ? 'Guruhni tahrirlash' : 'Yangi guruh'"
      width="560px"
      align-center
      destroy-on-close
      class="bp-grp-dialog"
      @update:model-value="$emit('update:modelValue', $event)"
      @closed="langErrors = []"
  >
    <!-- Live preview of the group's chart row -->
    <div class="bp-grp-preview" :style="{'--grp-color': form.color || '#64748B'}">
      <span class="bp-grp-preview__band"/>
      <span class="bp-grp-preview__title">{{ previewTitle }}</span>
      <span class="bp-grp-preview__bar">Band namunasi</span>
      <span v-if="!form.is_active" class="bp-grp-preview__badge">Nofaol</span>
    </div>

    <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="bp-grp-form">
      <div class="bp-field-label">Nomi <span class="bp-field-label__req">(barcha tillarda)</span></div>
      <el-tabs v-model="activeLang" class="bp-lang-tabs">
        <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
          <template #label>
            <span class="bp-lang-tab" :class="{'is-error': langErrors.includes(lt.code)}">
              {{ lt.tabLabel }}
              <span
                  class="bp-lang-tab__dot"
                  :class="{'is-filled': !!form.title[lt.code]}"
                  :title="form.title[lt.code] ? 'To\'ldirilgan' : 'Bo\'sh'"
              />
            </span>
          </template>
          <el-form-item :prop="'title.' + lt.code" label-width="0">
            <el-input
                v-model="form.title[lt.code]"
                :placeholder="'Guruh nomi — ' + langLabels[lt.code]"
                clearable
                maxlength="120"
                @input="clearLangError(lt.code)"
            />
          </el-form-item>
        </el-tab-pane>
      </el-tabs>

      <div class="bp-field-label">Rang</div>
      <div class="bp-swatches">
        <button
            v-for="c in colorPalette"
            :key="c"
            type="button"
            class="bp-swatch"
            :class="{'is-active': form.color && form.color.toLowerCase() === c.toLowerCase()}"
            :style="{background: c}"
            :title="c"
            @click="form.color = c"
        >
          <span v-if="form.color && form.color.toLowerCase() === c.toLowerCase()" class="bp-swatch__check">✓</span>
        </button>
        <el-color-picker v-model="form.color" class="bp-swatch-custom" :predefine="colorPalette"/>
        <span class="bp-swatch-hex">{{ form.color || defaultColorHex }}</span>
      </div>

      <el-row :gutter="16" class="bp-grp-row">
        <el-col :span="12">
          <div class="bp-field-label">Tartib raqami</div>
          <el-input-number v-model="form.order_number" :min="0" controls-position="right" style="width: 100%"/>
        </el-col>
        <el-col :span="12">
          <div class="bp-field-label">Holati</div>
          <el-switch
              v-model="form.is_active"
              inline-prompt
              active-text="Faol"
              inactive-text="Nofaol"
              style="--el-switch-on-color: #16A34A"
          />
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <el-button plain :disabled="submitting" @click="$emit('update:modelValue', false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="submitting" @click="onSubmit">
        {{ isEdit ? "Saqlash" : "Qo'shish" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import {ElMessage, ElNotification} from "element-plus";
import {
  API_BASE,
  LANG_TABS,
  LANG_LABELS,
  fillMultiLang,
  displayLangText,
  GROUP_COLOR_PALETTE,
  DEFAULT_GROUP_COLOR,
} from "./budgetProcessTypes";

const requiredRule = {required: true, message: "Majburiy maydon", trigger: "blur"};

export default {
  name: "BudgetProcessGroupDialog",
  props: {
    modelValue: {type: Boolean, default: false},
    // null = create mode, otherwise the group row being edited.
    group: {type: Object, default: null},
    // How many groups already exist — used to cycle the default color for a
    // NEW group through the palette instead of always defaulting to the same
    // color (which is exactly what happens if the admin never touches the
    // color picker).
    groupsCount: {type: Number, default: 0},
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      submitting: false,
      activeLang: "uz_lat",
      langErrors: [],
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
      colorPalette: GROUP_COLOR_PALETTE,
      defaultColorHex: DEFAULT_GROUP_COLOR,
      form: this.buildForm(null),
      rules: {
        "title.uz_lat": [requiredRule],
        "title.uz_cyr": [requiredRule],
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.group;
    },
    previewTitle() {
      return displayLangText(this.form.title) || "Guruh nomi";
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      this.activeLang = "uz_lat";
      this.langErrors = [];
      this.form = this.buildForm(this.group);
    },
  },
  methods: {
    buildForm(group) {
      const defaultColor = GROUP_COLOR_PALETTE[this.groupsCount % GROUP_COLOR_PALETTE.length];
      return {
        title: fillMultiLang(group?.title),
        color: group?.color || defaultColor,
        order_number: group?.order_number ?? 0,
        is_active: group?.is_active !== false,
      };
    },
    clearLangError(code) {
      this.langErrors = this.langErrors.filter((c) => c !== code);
    },
    handleApiError(error, fallbackTitle) {
      const data = error?.response?.data;
      ElNotification({
        title: fallbackTitle,
        message: data?.message || "Iltimos, qayta urinib ko'ring.",
        type: "error",
      });
      if (data?.invalid_args && typeof data.invalid_args === "object") {
        Object.entries(data.invalid_args).forEach(([field, detail]) => {
          ElMessage.error(`${field}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
        });
      }
    },
    onSubmit() {
      this.langErrors = [];
      this.$refs.formRef.validate((valid, invalidFields) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          if (invalidFields) {
            this.langErrors = Object.keys(invalidFields)
                .filter((key) => key.startsWith("title."))
                .map((key) => key.split(".")[1]);
            if (this.langErrors[0]) this.activeLang = this.langErrors[0];
          }
          return;
        }
        const payload = {
          title: {...this.form.title},
          color: this.form.color || DEFAULT_GROUP_COLOR,
          order_number: Number(this.form.order_number) || 0,
          is_active: this.form.is_active,
        };
        this.submitting = true;
        const request = this.isEdit
            ? this.$http.put(`${API_BASE}/groups/${this.group.id}`, payload)
            : this.$http.post(`${API_BASE}/groups`, payload);
        request
            .then(() => {
              ElMessage({type: "success", message: this.isEdit ? "Guruh yangilandi" : "Guruh yaratildi"});
              this.$emit("update:modelValue", false);
              this.$emit("saved");
            })
            .catch((error) => this.handleApiError(error, "Saqlashda xatolik"))
            .finally(() => {
              this.submitting = false;
            });
      });
    },
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

.bp-grp-dialog {
  :deep(.el-dialog__title) {
    font-family: "Montserrat-Bold", sans-serif;
    color: $navy;
  }
}

/* ---- live preview ---- */
.bp-grp-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 20px;
  background: #fafbfd;
  border: 1px solid $border;
  border-radius: 10px;
  overflow: hidden;

  &__band {
    width: 6px;
    height: 30px;
    border-radius: 4px;
    background: var(--grp-color);
    flex-shrink: 0;
  }

  &__title {
    font-family: "Montserrat-Medium", sans-serif;
    font-weight: 600;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }

  &__bar {
    margin-left: auto;
    padding: 6px 16px;
    border-radius: 6px;
    background: var(--grp-color);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &__badge {
    padding: 2px 8px;
    border-radius: 20px;
    background: #e6a23c;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
  }
}

/* ---- field labels ---- */
.bp-field-label {
  margin: 4px 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #4a5468;

  &__req {
    font-weight: 400;
    color: $muted;
  }
}

.bp-lang-tabs {
  margin-bottom: 8px;

  :deep(.el-tabs__item.is-active) {
    color: $brand;
  }

  :deep(.el-tabs__active-bar) {
    background-color: $brand;
  }
}

.bp-lang-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.is-error {
    color: #f56c6c;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #dcdfe6;
    transition: background 0.15s ease;

    &.is-filled {
      background: #16a34a;
    }
  }
}

/* ---- swatches ---- */
.bp-swatches {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.bp-swatch {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
  }

  &.is-active {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(10, 25, 78, 0.35);
  }

  &__check {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }
}

.bp-swatch-custom {
  margin-left: 4px;
}

.bp-swatch-hex {
  color: $muted;
  font-size: 12px;
  font-family: "SFMono-Regular", Consolas, monospace;
}

.bp-grp-row {
  margin-top: 4px;
}
</style>
